/**
 * ProductPreview Component
 *
 * Container for the CanvasEditor with product info badges and position toggle.
 * The CanvasEditor now renders the mockup as a Fabric.js layer, eliminating
 * the need for the separate MockupRenderer overlay system.
 *
 * Architecture:
 * - ProductPreview: Container with badges and position toggle
 * - CanvasEditor: Full-mockup canvas with embedded mockup layer
 */

import { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_MAX_THREAD_COLORS } from '@designdeck/shared';
import { useProductSelectionStore } from '@/stores';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import { useProducts } from '@/hooks';
import {
  transformProduct,
  findTemplateByPosition,
  getDefaultTemplate,
  getConfiguredPositionTemplates,
} from '@/lib/product-transformer';
import { Package, RotateCcw } from 'lucide-react';
import { CanvasEditor } from './CanvasEditor';
import type { ControlLayout } from './controls';
import { createCanvasConfig } from '@/lib/canvasConfigAdapter';
import { buildPrintContextParamsFromTemplate } from '@/lib/printContextBuilder';

/**
 * ProductPreview component
 *
 * Container for the CanvasEditor with product information display.
 * - When no product is focused: Shows CanvasEditor with default config
 * - When a product is focused: Shows CanvasEditor with product mockup
 */
export function ProductPreview({
  hideOverlayChips = false,
  controlScreenSize,
  controlLayout,
  fitToProduct,
  maxScaleFactor,
}: {
  /**
   * Hides the floating product/position chips over the canvas. Direct Sale
   * passes true — its chrome (sidebar, position pills) already carries that
   * information and the chips collide with the mock's clean canvas.
   */
  hideOverlayChips?: boolean;
  /**
   * Size of the object handles in screen pixels, passed through to the
   * editor. Direct Sale's phone layout asks for thumb-sized handles.
   */
  controlScreenSize?: number;
  /** Arrangement of the object handles, passed through to the editor. */
  controlLayout?: ControlLayout;
  /** Zoom in on the product's own box, passed through to the editor. */
  fitToProduct?: boolean;
  /** Upper bound for the display factor, passed through to the editor. */
  maxScaleFactor?: number;
} = {}) {
  const { t } = useTranslation();
  // Store values
  const focusedProductId = useProductSelectionStore(
    (state) => state.focusedProductId
  );
  const focusedColorId = useProductSelectionStore(
    (state) => state.focusedColorId
  );
  const activePosition = useProductSelectionStore(
    (state) => state.activePosition
  );
  const setActivePosition = useProductSelectionStore(
    (state) => state.setActivePosition
  );

  const { data: apiProducts } = useProducts();
  const selections = useProductSelectionStore((state) => state.selections);

  // Find the focused product
  const focusedProduct = useMemo(() => {
    if (!focusedProductId || !apiProducts) return null;

    const apiProduct = apiProducts.find((p) => p.id === focusedProductId);
    if (!apiProduct) return null;

    return transformProduct(apiProduct);
  }, [focusedProductId, apiProducts]);

  // Get the selected color
  const selectedColor = useMemo(() => {
    if (!focusedProduct) return null;

    if (focusedColorId) {
      const color = focusedProduct.colors.find((c) => c.id === focusedColorId);
      if (color) return color;
    }

    return focusedProduct.colors[0] || null;
  }, [focusedProduct, focusedColorId]);

  // Get active template based on position
  const activeTemplate = useMemo(() => {
    if (!focusedProduct || focusedProduct.mockupTemplates.length === 0) {
      return null;
    }

    const template = findTemplateByPosition(
      focusedProduct.mockupTemplates,
      activePosition
    );

    return template || getDefaultTemplate(focusedProduct.mockupTemplates);
  }, [focusedProduct, activePosition]);

  // Available positions for toggle - only the ones the product configures
  const availablePositions = useMemo(() => {
    if (!focusedProduct) return [];

    return getConfiguredPositionTemplates(focusedProduct.mockupTemplates).map(
      (t) => ({ slug: t.positionKey, id: t.productPositionId })
    );
  }, [focusedProduct]);

  // An active position the product does not configure (e.g. a sleeve mockup
  // without a ProductPosition) is no longer offered, so the canvas must not
  // stay on it - fall back to the first configured position.
  useEffect(() => {
    if (availablePositions.length === 0) return;
    if (availablePositions.some((p) => p.slug === activePosition)) return;

    const fallback = availablePositions[0];
    if (!fallback) return;

    const template = findTemplateByPosition(
      focusedProduct?.mockupTemplates ?? [],
      fallback.slug
    );

    if (!template) return;
    setActivePosition(
      fallback.slug,
      fallback.id,
      buildPrintContextParamsFromTemplate(template)
    );
  }, [availablePositions, activePosition, focusedProduct, setActivePosition]);

  // Position toggle handler
  const handleTogglePosition = useCallback(() => {
    const currentIndex = availablePositions.findIndex(
      (p) => p.slug === activePosition
    );
    const nextIndex = (currentIndex + 1) % availablePositions.length;
    const nextPosition = availablePositions[nextIndex];

    const nextTemplate = focusedProduct?.mockupTemplates.find(
      (t) => t.positionKey === nextPosition?.slug
    );

    if (!nextPosition || !nextTemplate) return;
    setActivePosition(
      nextPosition.slug,
      nextPosition.id,
      buildPrintContextParamsFromTemplate(nextTemplate)
    );
  }, [availablePositions, activePosition, setActivePosition, focusedProduct]);

  // Keep activePrintContext in sync with the focused product template.
  // CanvasEditor uses createCanvasConfig offsets; without this sync,
  // relativeState extraction uses stale/wrong offsets → product cards mismatch.
  useEffect(() => {
    if (!activeTemplate) return;

    const positionId =
      availablePositions.find((p) => p.slug === activePosition)?.id ?? null;

    setActivePosition(
      activePosition,
      positionId,
      buildPrintContextParamsFromTemplate(activeTemplate)
    );
  }, [activeTemplate, activePosition, availablePositions, setActivePosition]);

  // Find the raw API product to access hsCode
  const apiProduct = useMemo(
    () =>
      focusedProductId
        ? apiProducts?.find((p) => p.id === focusedProductId)
        : null,
    [focusedProductId, apiProducts]
  );

  // Create canvas config from template and color
  const canvasConfig = useMemo(() => {
    // No calibrated template, no area: the editor is not mounted (R13).
    if (!activeTemplate) return null;
    return createCanvasConfig(
      activeTemplate,
      selectedColor,
      apiProduct?.hsCode
    );
  }, [activeTemplate, selectedColor, apiProduct?.hsCode]);

  // Check if color is heathered
  const isHeathered = selectedColor?.isHeathered ?? false;

  // Check if the focused product supports embroidery on any position
  const activeDecorationMethod = useMemo(() => {
    if (!focusedProductId || !apiProducts) return null;
    const apiProduct = apiProducts.find((p) => p.id === focusedProductId);
    const positions = apiProduct?.positions;
    if (!positions || positions.length === 0) return null;

    const activePos = positions.find((pos) => pos.slug === activePosition);
    const positionsToCheck = activePos ? [activePos] : positions;

    for (const pos of positionsToCheck) {
      if (pos.decorationMethods) return pos.decorationMethods;
    }

    for (const pos of positions) {
      if (pos.decorationMethods) return pos.decorationMethods;
    }
    return null;
  }, [focusedProductId, apiProducts, activePosition]);

  const isEmbroidery = activeDecorationMethod?.code === 'EMBROIDERY';

  // Sync embroidery product state with embroidery store
  const setEmbroideryProduct = useEmbroideryStore(
    (s) => s.setEmbroideryProduct
  );
  const setEmbroideryMaxColors = useEmbroideryStore((s) => s.setMaxColors);
  const setEmbroideryMinDpi = useEmbroideryStore((s) => s.setMinDpi);
  const clearEmbroidery = useEmbroideryStore((s) => s.clearEmbroidery);
  useEffect(() => {
    setEmbroideryProduct(isEmbroidery);
    if (!isEmbroidery) {
      clearEmbroidery();
    }
  }, [isEmbroidery, setEmbroideryProduct, clearEmbroidery]);

  // The ceiling is the strictest one in play, not the focused product's.
  // All active products share one master design, and only print-vs-embroidery
  // mixing is blocked — so several embroidery products with different limits can
  // be active at once, and a mapping that fits the focused one would otherwise
  // be applied to the others unchecked.
  const embroideryLimits = useMemo(() => {
    let lowestMaxColors: number | null = null;
    let minDpi: number | null = null;

    for (const product of apiProducts ?? []) {
      // Only products the seller actually put in the run. Scanning the whole
      // catalogue let a product they never selected lower their ceiling.
      if (!selections[product.id]?.isActive) continue;

      for (const position of product.positions ?? []) {
        const method = position.decorationMethods;
        if (method?.code !== 'EMBROIDERY') continue;
        if (typeof method.maxColors === 'number') {
          // Seeded from the first configured value rather than from the
          // fallback, which would otherwise only ever lower a real limit.
          lowestMaxColors =
            lowestMaxColors === null
              ? method.maxColors
              : Math.min(lowestMaxColors, method.maxColors);
        }
        if (typeof method.minDpi === 'number') {
          minDpi =
            minDpi === null ? method.minDpi : Math.max(minDpi, method.minDpi);
        }
      }
    }
    return {
      maxColors: lowestMaxColors ?? DEFAULT_MAX_THREAD_COLORS,
      minDpi,
    };
  }, [apiProducts, selections]);

  useEffect(() => {
    if (!isEmbroidery) return;
    setEmbroideryMaxColors(embroideryLimits.maxColors);
    setEmbroideryMinDpi(embroideryLimits.minDpi);
  }, [
    isEmbroidery,
    embroideryLimits,
    setEmbroideryMaxColors,
    setEmbroideryMinDpi,
  ]);

  return (
    <div className="flex-1 flex items-stretch justify-center p-4 min-h-0 relative">
      {/* Product Info Badge */}
      {!hideOverlayChips && focusedProduct && (
        <div className="absolute top-4 left-4 z-30">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-paper-2/90 backdrop-blur-sm rounded-control border border-line">
            <Package className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-ink-2">
              {focusedProduct.name}
            </span>
            {selectedColor && (
              <div
                className="w-4 h-4 rounded-full border border-line"
                style={{ backgroundColor: selectedColor.hex }}
                title={selectedColor.name}
              />
            )}
            {isHeathered && (
              <span className="text-xs text-mute">
                {t('creator.mockup.heather')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Embroidery Badge - shown when product uses embroidery decoration */}
      {focusedProduct && isEmbroidery && (
        <div className="absolute top-14 left-4 z-30">
          <div className="flex items-center gap-1.5 px-3 py-1 bg-[color-mix(in_srgb,var(--warning)_10%,transparent)]/90 backdrop-blur-sm rounded-control border border-warning">
            <span className="text-xs font-semibold text-warning">
              {t('creator.mockup.embroideryBadge')}
            </span>
            {activeDecorationMethod?.name &&
              activeDecorationMethod.name !== 'Embroidery' && (
                <span className="text-xs text-warning">
                  ({activeDecorationMethod.name})
                </span>
              )}
          </div>
        </div>
      )}

      {/* Position Toggle Button */}
      {!hideOverlayChips && availablePositions.length > 1 && (
        <div className="absolute top-4 right-4 z-30">
          <button
            onClick={handleTogglePosition}
            className="flex items-center gap-2 px-3 py-1.5 bg-paper-2/90 backdrop-blur-sm rounded-control border border-line hover:bg-paper-2 transition-colors"
            title={t('creator.mockup.switchPosition')}
          >
            <RotateCcw className="w-4 h-4 text-ink-2" />
            <span className="text-sm font-medium text-ink-2 capitalize">
              {activePosition === 'front'
                ? t('creator.positions.front')
                : activePosition === 'back'
                  ? t('creator.positions.back')
                  : activePosition}
            </span>
          </button>
        </div>
      )}

      {/* CanvasEditor - Full-Mockup Canvas */}
      {canvasConfig ? (
        <CanvasEditor
          config={canvasConfig}
          className="flex-1"
          controlScreenSize={controlScreenSize}
          controlLayout={controlLayout}
          fitToProduct={fitToProduct}
          maxScaleFactor={maxScaleFactor}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="text-sm text-ink-2" role="status">
            {t('creator.mockup.areaUnavailable')}
          </p>
        </div>
      )}
    </div>
  );
}
